import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { ConceptLinkerValidatorModel } from 'models/ConceptLinkerValidatorModel';
import { IConceptLinkerValidatorCreate } from 'types/conceptLinkerValidator';
import { TextField, Checkbox, FormControlLabel } from '@material-ui/core';
import { IRefIds } from 'constants/refs';
import CourseSelect from 'components/selects/CourseSelect';
import VocSearchModal from 'components/modal/VocSearchModal';
import PanelSearchModal from 'components/modal/PanelSearchModal';
import CompoundSearchModal from 'components/modal/CompoundSearchModal';
import CulturalVocSearchModal from 'components/modal/CulturalVocSearchModal';
import ConceptCard from 'components/common/ConceptCard';
import ModuleContext from 'CMFW/context/ModuleContext';
import { IVoc } from '../../models/VocabularyModel';
import { IPanel } from '../../models/PanelsModel';
import { ICompound } from '../../models/CompoundModel';
import { ICulturalVocs } from '../../types/culturalvocs';

interface IProps {
  model: ConceptLinkerValidatorModel;
  onClose: () => void;
  onSuccess: (validationId: number) => void;
}

interface ISelectedConcept {
  ref: number;
  refId: number;
  name: string;
}

type ConceptType = 'vocabulary' | 'panel' | 'compound' | 'cultural';

const ConceptLinkerValidatorCreate: React.FC<IProps> = ({ model, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [isGlobal, setIsGlobal] = useState(true);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [mainConcept, setMainConcept] = useState<ISelectedConcept | null>(null);
  const [alternativeConcepts, setAlternativeConcepts] = useState<ISelectedConcept[]>([]);
  const [showConceptModal, setShowConceptModal] = useState(false);
  const [conceptType, setConceptType] = useState<ConceptType | null>(null);
  const [isSelectingAlternative, setIsSelectingAlternative] = useState(false);
  const [includeCompounds, setIncludeCompounds] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleScopeChange = (scope: 'global' | 'course') => {
    setIsGlobal(scope === 'global');
    if (scope === 'global') {
      setCourseId(null);
      // Filter out non-vocabulary concepts when switching to global
      if (mainConcept && mainConcept.ref !== IRefIds.vocs) {
        setMainConcept(null);
      }
      // Filter alternative concepts to keep only vocabulary
      setAlternativeConcepts(alternativeConcepts.filter((concept) => concept.ref === IRefIds.vocs));
    }
  };

  const handleCourseChange = (value: number) => {
    setCourseId(value);
  };

  const handleAddMainConcept = () => {
    setIsSelectingAlternative(false);
    if (isGlobal) {
      // For global, only vocabulary is allowed
      setConceptType('vocabulary');
      setShowConceptModal(true);
    }
    // For course, user needs to select concept type from dropdown first
  };

  const handleAddAlternativeConcept = () => {
    setIsSelectingAlternative(true);
    if (isGlobal) {
      setConceptType('vocabulary');
      setShowConceptModal(true);
    }
    // For course, user needs to select concept type from dropdown first
  };

  const handleConceptTypeChange = (type: ConceptType, isForAlternative = false) => {
    setIsSelectingAlternative(isForAlternative);
    setConceptType(type);
    if (type) {
      setShowConceptModal(true);
    }
  };

  const handleVocSelect = (voc: IVoc) => {
    const concept = {
      ref: IRefIds.vocs,
      refId: voc.id,
      name: voc.title,
    };

    if (isSelectingAlternative) {
      setAlternativeConcepts([...alternativeConcepts, concept]);
    } else {
      setMainConcept(concept);
    }
    setShowConceptModal(false);
  };

  const handlePanelSelect = (panel: IPanel) => {
    const concept = {
      ref: IRefIds.panels,
      refId: panel.id,
      name: panel.title,
    };

    if (isSelectingAlternative) {
      setAlternativeConcepts([...alternativeConcepts, concept]);
    } else {
      setMainConcept(concept);
    }
    setShowConceptModal(false);
  };

  const handleCompoundSelect = (compound: ICompound) => {
    const concept = {
      ref: IRefIds.compounds,
      refId: compound.id,
      name: compound.title,
    };

    if (isSelectingAlternative) {
      setAlternativeConcepts([...alternativeConcepts, concept]);
    } else {
      setMainConcept(concept);
    }
    setShowConceptModal(false);
  };

  const handleCulturalVocSelect = (culturalVoc: ICulturalVocs) => {
    const concept = {
      ref: IRefIds.cultural,
      refId: culturalVoc.id,
      name: culturalVoc.name,
    };

    if (isSelectingAlternative) {
      setAlternativeConcepts([...alternativeConcepts, concept]);
    } else {
      setMainConcept(concept);
    }
    setShowConceptModal(false);
  };

  const removeAlternativeConcept = useCallback((index: number) => {
    setAlternativeConcepts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleModalClose = () => {
    setShowConceptModal(false);
    setConceptType(null);
    setIsSelectingAlternative(false);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!isGlobal && !courseId) {
      newErrors.course = 'Course is required when not global';
    }

    if (!mainConcept) {
      newErrors.concept = 'Main concept is required';
    }

    // Check for duplicates between main and alternatives
    if (mainConcept && alternativeConcepts.length > 0) {
      const isDuplicate = alternativeConcepts.some(
        (alt) => alt.ref === mainConcept.ref && alt.refId === mainConcept.refId,
      );
      if (isDuplicate) {
        newErrors.alternatives = 'Main concept cannot appear in alternatives';
      }
    }

    // Check for duplicates within alternatives
    const alternativeKeys = alternativeConcepts.map((c) => `${c.ref}-${c.refId}`);
    const uniqueKeys = new Set(alternativeKeys);
    if (uniqueKeys.size !== alternativeKeys.length) {
      newErrors.alternatives = 'Duplicate concepts in alternatives';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    if (!mainConcept) return;

    setSaving(true);
    try {
      const data: IConceptLinkerValidatorCreate = {
        name: name.trim(),
        item_ref: mainConcept.ref,
        item_ref_id: mainConcept.refId,
        courseId: isGlobal ? 0 : courseId || 0,
        alternative_items: alternativeConcepts.map((c) => ({
          ref: c.ref,
          refId: c.refId,
        })),
        includeCompounds: (mainConcept.ref === IRefIds.vocs || mainConcept.ref === IRefIds.panels) ? includeCompounds : undefined,
      };

      const response = await model.createValidation(data);
      if (response.data) {
        onSuccess(response.data.id);
      }
    } catch (error) {
      console.error('Failed to create validation:', error);
      setErrors({ submit: 'Failed to create validation' });
    } finally {
      setSaving(false);
    }
  };

  const renderConceptModal = () => {
    if (!showConceptModal || !conceptType) return null;

    switch (conceptType) {
      case 'vocabulary':
        return <VocSearchModal onClose={handleModalClose} onChooseVoc={handleVocSelect} />;
      case 'panel':
        return <PanelSearchModal courseId={courseId} onCancel={handleModalClose} onSelect={handlePanelSelect} />;
      case 'compound':
        return (
          <CompoundSearchModal courseId={courseId} onClose={handleModalClose} onChooseCompound={handleCompoundSelect} />
        );
      case 'cultural':
        return (
          <CulturalVocSearchModal
            courseId={courseId}
            onClose={handleModalClose}
            onChooseCulturalVoc={handleCulturalVocSelect}
          />
        );
      default:
        return null;
    }
  };

  const renderConceptCard = (concept: ISelectedConcept, onRemove: () => void) => {
    // For global (vocabulary only) or course-specific concepts
    const effectiveCourseId = isGlobal ? 0 : (courseId || 0);
    
    return (
      <ConceptCardWrapper>
        <ConceptCard 
          conceptRef={concept.ref} 
          refId={concept.refId} 
          courseId={effectiveCourseId}
        />
        <ConceptName>{concept.name}</ConceptName>
        <RemoveButton onClick={onRemove}>×</RemoveButton>
      </ConceptCardWrapper>
    );
  };

  return (
    <Container>
      <Header>
        <Title>Create New Concept Validation</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <Content>
        <Section>
          <SectionTitle>1. Validation Name</SectionTitle>
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter validation name"
            error={!!errors.name}
            helperText={errors.name}
            variant="outlined"
            fullWidth
          />
        </Section>

        <Section>
          <SectionTitle>2. Scope</SectionTitle>
          <ScopeButtons>
            <ScopeButton active={isGlobal} onClick={() => handleScopeChange('global')}>
              Global (Vocabulary Only)
            </ScopeButton>
            <ScopeButton active={!isGlobal} onClick={() => handleScopeChange('course')}>
              Course Specific
            </ScopeButton>
          </ScopeButtons>
          {!isGlobal && (
            <CourseSelectWrapper>
              <CourseSelect courseId={courseId || undefined} onChange={handleCourseChange} />
              {errors.course && <ErrorText>{errors.course}</ErrorText>}
            </CourseSelectWrapper>
          )}
        </Section>

        {(isGlobal || (!isGlobal && courseId)) && (
          <Section>
            <SectionTitle>3. Main Concept (Required)</SectionTitle>
            <ConceptBox>
              {mainConcept ? (
                renderConceptCard(mainConcept, () => setMainConcept(null))
              ) : (
                <>
                  {isGlobal ? (
                    <AddConceptButton onClick={handleAddMainConcept}>+ Add Vocabulary</AddConceptButton>
                  ) : (
                    <ConceptTypeDropdown
                      onChange={(e) => {
                        const type = e.target.value as ConceptType;
                        if (type) {
                          handleConceptTypeChange(type, false);
                          e.target.value = ''; // Reset dropdown after selection
                        }
                      }}
                    >
                      <option value="">+ Add concept (select type)...</option>
                      <option value="vocabulary">Vocabulary</option>
                      <option value="panel">Panel</option>
                      <option value="compound">Compound</option>
                      <option value="cultural">Cultural Vocabulary</option>
                    </ConceptTypeDropdown>
                  )}
                </>
              )}
            </ConceptBox>
            {errors.concept && <ErrorText>{errors.concept}</ErrorText>}
            
            {/* Show include compounds checkbox only for Vocabulary or Panels */}
            {mainConcept && (mainConcept.ref === IRefIds.vocs || mainConcept.ref === IRefIds.panels) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCompounds}
                    onChange={(e) => setIncludeCompounds(e.target.checked)}
                    color="primary"
                  />
                }
                label="Include compounds of this item"
                style={{ marginTop: '10px' }}
              />
            )}
          </Section>
        )}

        {(isGlobal || (!isGlobal && courseId)) && (
          <Section>
            <SectionTitle>4. Alternative Concepts (Optional)</SectionTitle>
            <ConceptsListContainer>
              {alternativeConcepts.map((concept, index) => (
                <React.Fragment key={`${concept.ref}-${concept.refId}`}>
                  {renderConceptCard(concept, () => removeAlternativeConcept(index))}
                </React.Fragment>
              ))}
              {isGlobal ? (
                <AddConceptButton onClick={handleAddAlternativeConcept}>+ Add Alternative</AddConceptButton>
              ) : (
                <ConceptTypeDropdown
                  onChange={(e) => {
                    const type = e.target.value as ConceptType;
                    if (type) {
                      handleConceptTypeChange(type, true);
                      e.target.value = ''; // Reset dropdown after selection
                    }
                  }}
                >
                  <option value="">+ Add alternative (select type)...</option>
                  <option value="vocabulary">Vocabulary</option>
                  <option value="panel">Panel</option>
                  <option value="compound">Compound</option>
                  <option value="cultural">Cultural Vocabulary</option>
                </ConceptTypeDropdown>
              )}
            </ConceptsListContainer>
            {errors.alternatives && <ErrorText>{errors.alternatives}</ErrorText>}
          </Section>
        )}

        <Note>
          Note: When alternatives are provided, AI will compare all concepts and suggest the best match for each phrase.
        </Note>

        {errors.submit && <ErrorText>{errors.submit}</ErrorText>}
      </Content>

      <Footer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <CreateButton onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create Validation'}
        </CreateButton>
      </Footer>

      {renderConceptModal()}
    </Container>
  );
};

export default ConceptLinkerValidatorCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 18px;
  color: #333;
`;

const ScopeButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const ScopeButton = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  border: 1px solid ${(props) => (props.active ? '#007bff' : '#ddd')};
  background-color: ${(props) => (props.active ? '#007bff' : 'white')};
  color: ${(props) => (props.active ? 'white' : '#333')};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.active ? '#0056b3' : '#f0f0f0')};
  }
`;

const CourseSelectWrapper = styled.div`
  margin-top: 10px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const AddConceptButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const ConceptCardWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  max-width: 300px;

  ${RemoveButton} {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #f8f9fa;
    color: #6c757d;
    border: 1px solid #dee2e6;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    z-index: 1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s;

    &:hover {
      background-color: #e9ecef;
      color: #495057;
      border-color: #adb5bd;
    }
  }
`;

const ConceptBox = styled.div`
  border: 2px dashed #ddd;
  border-radius: 4px;
  padding: 20px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: #fafafa;

  ${ConceptCardWrapper} {
    max-width: none;
    width: auto;
  }
`;

const SelectedConcept = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 15px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  min-width: 200px;
  max-width: 300px;

  ${RemoveButton} {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #f8f9fa;
    color: #6c757d;
    border: 1px solid #dee2e6;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    z-index: 1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s;

    &:hover {
      background-color: #e9ecef;
      color: #495057;
      border-color: #adb5bd;
    }
  }
`;

const ConceptName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #333;
  line-height: 1.4;
`;

const ConceptInfo = styled.div`
  color: #666;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Note = styled.div`
  padding: 15px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  color: #856404;
  font-size: 14px;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConceptsListContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 15px;
  padding: 20px;
  border: 2px dashed #ddd;
  border-radius: 4px;
  min-height: 120px;
  align-items: flex-start;
  align-content: flex-start;
  background-color: #fafafa;

  ${ConceptCardWrapper} {
    max-width: none;
    width: auto;
  }
`;

const ConceptTypeDropdown = styled.select`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  width: 100%;

  &:hover {
    background-color: #e0e0e0;
  }

  option {
    background-color: white;
  }
`;

const ConceptTypeLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;
